import { zodResolver } from "@hookform/resolvers/zod";
import { Avatar } from "@package/subframe/components/Avatar";
import { Button } from "@package/subframe/components/Button";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherExternalLink, FeatherEye, FeatherEyeOff } from "@subframe/core";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

import { Spinner } from "@/components/ui/spinner";
import { env } from "@/config/env";
import { PinLoginShell } from "@/features/device-setup/PinLoginShell";
import { resolveLoginScreen } from "@/features/device-setup/resolve-login-screen";
import { SetupScreen } from "@/features/device-setup/SetupScreen";
import { useDeviceLink } from "@/features/device-setup/use-device-link";
import { useIsTouchDevice } from "@/hooks/use-is-touch-device";
import { signIn } from "@/lib/auth";

// Email domain for staff members (they don't have real emails)
const STAFF_EMAIL_DOMAIN = env.VITE_STAFF_EMAIL_DOMAIN || "naud-staff.local";

const userAuthSchema = z.object({
  identifier: z.string().min(1, "Email or username is required"),
  password: z.string().min(1, "Password is required"),
});

type FormData = z.infer<typeof userAuthSchema>;

// Convert identifier to email if it's a username
function toEmail(identifier: string): string {
  const trimmed = identifier.trim().toLowerCase();
  // If it contains @, treat as email; otherwise treat as username
  if (trimmed.includes("@")) {
    return trimmed;
  }
  return `${trimmed}@${STAFF_EMAIL_DOMAIN}`;
}

export const Route = createFileRoute("/_auth/login")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    return {
      redirect: search.redirect ? String(search.redirect) : undefined,
    };
  },
  component: LoginPage,
});

function DesktopLoginForm() {
  const { redirect } = useSearch({ from: "/_auth/login" });
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(userAuthSchema),
  });

  const identifierValue = watch("identifier", "");
  const passwordValue = watch("password", "");

  async function onSubmit(data: FormData) {
    setIsLoading(true);

    try {
      const email = toEmail(data.identifier);
      const user = await signIn(email, data.password);

      if (!user) {
        throw new Error("Login failed");
      }

      // Store session start time and login method for analytics
      const isUsernameLogin = !data.identifier.includes("@");
      sessionStorage.setItem("session_start_time", Date.now().toString());
      sessionStorage.setItem(
        "login_method",
        isUsernameLogin ? "username_password" : "email_password",
      );

      toast.success("Login successful!");

      // The session has not landed yet, so the role is still UNKNOWN and any
      // role-based choice here would send nursery users to the dashboard. Only
      // an explicit destination is knowable; otherwise the _auth layout routes
      // by role once the session arrives.
      if (redirect) {
        navigate({ to: redirect, replace: true });
      }
    } catch {
      toast.error("Invalid credentials. Please try again.");
    } finally {
      // Re-enable on success too: signing in can succeed while the session
      // fetch fails, and nothing would redirect away from a disabled form.
      setIsLoading(false);
    }
  }

  return (
    <div className="flex w-full items-center justify-center h-screen">
      {/* Left side - Testimonial */}
      <div className="flex flex-col items-start justify-end gap-6 self-stretch bg-primary-navy-800 pl-24 pr-12 pt-12 pb-32 w-1/2 mobile:hidden">
        <Avatar size="x-large" image="/images/testimonial-avatar.png">
          SK
        </Avatar>
        <div className="flex w-full flex-col items-start gap-6 max-w-[80%]">
          <span className="font-['Manrope'] text-[36px] font-[500] leading-[40px] tracking-wide text-white">
            &quot;This platform has transformed how we manage compliance at our
            nursery. The interface is intuitive and saves us countless hours
            every week.&quot;
          </span>
          <div className="flex flex-wrap items-end gap-2">
            <span className="font-['Manrope'] text-[20px] font-[700] leading-[24px] tracking-tight text-primary-rose">
              Sarah Kennedy
            </span>
            <span className="text-body font-body text-primary-rose">–</span>
            <span className="text-body font-body text-primary-rose">
              Director, Little Sprouts Nursery
            </span>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex flex-col items-center justify-center self-stretch bg-white px-12 py-12 relative w-1/2 mobile:h-auto mobile:grow mobile:shrink-0 mobile:basis-0 mobile:self-stretch mobile:px-6 mobile:py-6">
        <div className="flex w-full max-w-[384px] flex-col items-start gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2 mobile:absolute mobile:left-8 mobile:top-8">
            <img
              className="h-8 flex-none object-cover"
              src="/images/logo.svg"
              alt="Logo"
            />
          </div>

          <span className="text-heading-1 font-heading-1 text-neutral-900">
            Sign in
          </span>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex w-full flex-col items-start gap-6"
          >
            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="Username"
              error={!!errors.identifier}
              helpText={errors.identifier?.message}
            >
              <TextField.Input
                placeholder="Enter your username"
                value={identifierValue}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                disabled={isLoading}
                {...register("identifier")}
              />
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="Password"
              error={!!errors.password}
              helpText={errors.password?.message}
              iconRight={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="bg-transparent border-none cursor-pointer p-0"
                >
                  {showPassword ? <FeatherEye /> : <FeatherEyeOff />}
                </button>
              }
            >
              <TextField.Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={passwordValue}
                autoComplete="current-password"
                disabled={isLoading}
                {...register("password")}
              />
            </TextField>

            <Button
              className="h-12 w-full flex-none"
              variant="brand-primary"
              size="large"
              type="submit"
              disabled={isLoading}
              loading={isLoading}
            >
              Sign in
            </Button>
          </form>

          <div className="flex w-full items-center justify-between">
            <div className="flex flex-wrap items-start gap-1">
              <span className="text-caption font-caption text-neutral-900">
                Don&apos;t have an account?
              </span>
              <Link to="/register" search={{ redirect }}>
                <LinkButton variant="brand" size="small">
                  Sign up
                </LinkButton>
              </Link>
            </div>
            <Link to="/forgot-password">
              <LinkButton variant="brand" size="small">
                Forgot password?
              </LinkButton>
            </Link>
          </div>

          <div className="flex flex-wrap items-start justify-center gap-1">
            <span className="text-caption font-caption text-subtext-color">
              By clicking &quot;Sign in&quot; you agree to the
            </span>
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              <LinkButton
                variant="brand"
                size="small"
                iconRight={<FeatherExternalLink />}
              >
                Terms of Service
              </LinkButton>
            </a>
            <span className="text-caption font-caption text-subtext-color">
              and acknowledge the
            </span>
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              <LinkButton
                variant="brand"
                size="small"
                iconRight={<FeatherExternalLink />}
              >
                Privacy Notice
              </LinkButton>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Login routing by device type (NAU-226 Decision 3): a touch device (phone or
 * tablet) shows the setup screen when unlinked, or the PIN-login shell once it's
 * linked to a nursery; mouse-driven desktops keep the standard email/password
 * login (desktop login is unchanged per NAU-226/227). Keyed on `(pointer: coarse)`
 * rather than a width breakpoint so tablets are covered regardless of orientation.
 *
 * The whole device-setup / PIN flow is gated by `VITE_DEVICE_LINKING_ENABLED`
 * (off by default) so it stays dark until switched on per environment; while
 * off, every device — touch or desktop — gets the standard email/password login.
 *
 * The branch order itself lives in `resolveLoginScreen` so it can be unit-tested;
 * see that function for why a saved link outranks the pointer type.
 */
function LoginRouter() {
  const isTouchDevice = useIsTouchDevice();
  const { link, isLoading, refresh } = useDeviceLink();

  const screen = resolveLoginScreen({
    deviceLinkingEnabled: env.VITE_DEVICE_LINKING_ENABLED,
    isLoadingLink: isLoading,
    link,
    isTouchDevice,
  });

  switch (screen) {
    case "loading":
      return (
        <div className="flex h-screen items-center justify-center">
          <Spinner />
        </div>
      );
    case "device-setup":
      return <SetupScreen onLinked={refresh} />;
    case "pin":
      // `resolveLoginScreen` only returns "pin" when a link is present.
      return <PinLoginShell link={link!} onReset={refresh} />;
    default:
      return <DesktopLoginForm />;
  }
}

function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <Spinner />
        </div>
      }
    >
      <LoginRouter />
    </Suspense>
  );
}
